// Prompt Profiles - Default profiles for different categories
// Phase 5: Prompt Builder v2

import type { PromptProfile, PromptToggles } from '@aica/core';
import { buildXMLSystemPrompt, XML_USER_TEMPLATE } from './xml-templates';

// ============================================================================
// Default Toggles by Category
// ============================================================================

/** All toggles enabled — used for stable system-prompt generation (cache optimization) */
export const ALL_TOGGLES_ON: PromptToggles = {
  qualityScore: true,
  saleProbability: true,
  attentionFlag: true,
  topic: true,
  keywords: true,
  keyMoments: true,
  summary: true,
  structuredSummary: true,
  fillerWords: true,
  stopWords: true,
  operatorErrors: true,
  greetingIntro: true,
  needsQualification: true,
  objectionHandling: true,
  closingTechniques: true,
  sentiment: true,
  politeness: true,
  interruptions: true,
  clarity: true,
  crmCompleteness: true,
  strengthsImprovements: true,
  businessIntelligence: true,
  extractedCrmFields: true,
  agreements: true,
};

export const DEFAULT_SALES_TOGGLES: PromptToggles = {
  // A. Summary metrics
  qualityScore: true,
  saleProbability: true,
  attentionFlag: true,

  // B. Content analysis
  topic: true,
  keywords: true,
  keyMoments: true,
  summary: true,
  structuredSummary: true,

  // C. Speech analysis
  fillerWords: true,
  stopWords: true,
  operatorErrors: true,

  // D. Dialogue technique (CRITICAL for sales)
  greetingIntro: true,
  needsQualification: true,
  objectionHandling: true,
  closingTechniques: true,

  // E. Emotions
  sentiment: true,
  politeness: true,
  interruptions: true,
  clarity: true,

  // F. Compliance
  crmCompleteness: true,

  // G. Recommendations
  strengthsImprovements: true,

  // H. Business intelligence
  businessIntelligence: true,
};

export const DEFAULT_SUPPORT_TOGGLES: PromptToggles = {
  // A. Summary metrics
  qualityScore: true,
  saleProbability: false, // Not relevant for support
  attentionFlag: true,

  // B. Content analysis
  topic: true,
  keywords: true,
  keyMoments: true,
  summary: true,
  structuredSummary: true,

  // C. Speech analysis
  fillerWords: true,
  stopWords: true,
  operatorErrors: true,

  // D. Dialogue technique (adapted for support)
  greetingIntro: true,
  needsQualification: false,
  objectionHandling: false,
  closingTechniques: false,

  // E. Emotions (CRITICAL for support)
  sentiment: true,
  politeness: true,
  interruptions: true,
  clarity: true,

  // F. Compliance
  crmCompleteness: true,

  // G. Recommendations
  strengthsImprovements: true,

  // H. Business intelligence
  businessIntelligence: true,
};

export const DEFAULT_CHAT_TOGGLES: PromptToggles = {
  // A. Summary metrics
  qualityScore: true,
  saleProbability: true,
  attentionFlag: true,

  // B. Content analysis
  topic: true,
  keywords: true,
  keyMoments: false, // Chats don't have timestamps
  summary: true,
  structuredSummary: true,

  // C. Speech analysis (adapted for text)
  fillerWords: false, // Not applicable to text
  stopWords: true,
  operatorErrors: true,

  // D. Dialogue technique
  greetingIntro: true,
  needsQualification: true,
  objectionHandling: true,
  closingTechniques: true,

  // E. Emotions
  sentiment: true,
  politeness: true,
  interruptions: false, // Not applicable to text
  clarity: false, // Not applicable to text

  // F. Compliance
  crmCompleteness: true,

  // G. Recommendations
  strengthsImprovements: true,

  // H. Business intelligence
  businessIntelligence: true,
};

export const DEFAULT_COMPLAINT_TOGGLES: PromptToggles = {
  // A. Summary metrics
  qualityScore: true,
  saleProbability: false,
  attentionFlag: true,

  // B. Content analysis
  topic: true,
  keywords: true,
  keyMoments: true,
  summary: true,
  structuredSummary: true,

  // C. Speech analysis
  fillerWords: true,
  stopWords: true,
  operatorErrors: true,

  // D. Dialogue technique (adapted for complaints)
  greetingIntro: true,
  needsQualification: false,
  objectionHandling: true, // Handling complaint objections
  closingTechniques: false,

  // E. Emotions (CRITICAL for complaints)
  sentiment: true,
  politeness: true,
  interruptions: true,
  clarity: true,

  // F. Compliance
  crmCompleteness: true,

  // G. Recommendations
  strengthsImprovements: true,

  // H. Business intelligence
  businessIntelligence: true,
};

// ============================================================================
// Default Profiles Factory
// ============================================================================

/**
 * Canonical (English) name/description of the four base default profiles.
 * Exported so the UI can recognize an untouched default by its stored
 * name/description and render a localized label instead.
 */
export const BASE_PROFILE_TEXTS: Record<
  'sales-call' | 'support-call' | 'sales-chat' | 'complaint',
  { name: string; description: string }
> = {
  'sales-call': {
    name: 'Sales Call Analysis',
    description:
      'Comprehensive analysis of sales call transcripts with focus on sales techniques and conversion probability',
  },
  'support-call': {
    name: 'Support Call Analysis',
    description:
      'Analysis of customer support calls with focus on problem resolution and customer satisfaction',
  },
  'sales-chat': {
    name: 'Sales Chat Analysis',
    description:
      'Analysis of sales chat conversations with focus on messaging effectiveness and engagement',
  },
  complaint: {
    name: 'Complaint Analysis',
    description:
      'Analysis of customer complaints with focus on de-escalation, resolution, and risk assessment',
  },
};

/**
 * Create default prompt profiles for all supported categories.
 * Always uses XML-structured prompts (legacy plaintext removed).
 *
 * ARCHITECTURE: Profiles are organization-scoped (not project-scoped).
 * Projects select ONE profile via projects.prompt_profile_id.
 *
 * @param orgId - Organization ID to associate profiles with
 * @returns Array of default prompt profiles
 */
export function createDefaultProfiles(orgId: string): PromptProfile[] {
  const now = new Date().toISOString();

  // The system prompt depends on the base category (and channel), not on the
  // profile's toggles — see SystemBuilderOptions. The toggles each profile
  // carries below still matter: they drive the user prompt and the strict
  // schema.
  const buildPrompts = (
    category: 'sales-call' | 'support-call' | 'sales-chat' | 'complaint'
  ) => ({
    systemPrompt: buildXMLSystemPrompt({
      category,
      includeExamples: true,
    }),
    userTemplate: XML_USER_TEMPLATE,
  });

  const salesPrompts = buildPrompts('sales-call');
  const supportPrompts = buildPrompts('support-call');
  const chatPrompts = buildPrompts('sales-chat');
  const complaintPrompts = buildPrompts('complaint');

  return [
    {
      id: `${orgId}-sales-call`,
      orgId: orgId,
      name: BASE_PROFILE_TEXTS['sales-call'].name,
      description: BASE_PROFILE_TEXTS['sales-call'].description,
      category: 'sales-call',
      toggles: DEFAULT_SALES_TOGGLES,
      systemPrompt: salesPrompts.systemPrompt,
      userTemplate: salesPrompts.userTemplate,
      version: 1,
      createdAt: now,
    },
    {
      id: `${orgId}-support-call`,
      orgId: orgId,
      name: BASE_PROFILE_TEXTS['support-call'].name,
      description: BASE_PROFILE_TEXTS['support-call'].description,
      category: 'support-call',
      toggles: DEFAULT_SUPPORT_TOGGLES,
      systemPrompt: supportPrompts.systemPrompt,
      userTemplate: supportPrompts.userTemplate,
      version: 1,
      createdAt: now,
    },
    {
      id: `${orgId}-sales-chat`,
      orgId: orgId,
      name: BASE_PROFILE_TEXTS['sales-chat'].name,
      description: BASE_PROFILE_TEXTS['sales-chat'].description,
      category: 'sales-chat',
      toggles: DEFAULT_CHAT_TOGGLES,
      systemPrompt: chatPrompts.systemPrompt,
      userTemplate: chatPrompts.userTemplate,
      version: 1,
      createdAt: now,
    },
    {
      id: `${orgId}-complaint`,
      orgId: orgId,
      name: BASE_PROFILE_TEXTS.complaint.name,
      description: BASE_PROFILE_TEXTS.complaint.description,
      category: 'complaint',
      toggles: DEFAULT_COMPLAINT_TOGGLES,
      systemPrompt: complaintPrompts.systemPrompt,
      userTemplate: complaintPrompts.userTemplate,
      version: 1,
      createdAt: now,
    },
  ];
}

/**
 * Get default toggles for a specific category
 * @param category - Category name
 * @returns Default toggles for the category
 */
export function getDefaultToggles(
  category: 'sales-call' | 'support-call' | 'sales-chat' | 'complaint'
): PromptToggles {
  switch (category) {
    case 'sales-call':
      return DEFAULT_SALES_TOGGLES;
    case 'support-call':
      return DEFAULT_SUPPORT_TOGGLES;
    case 'sales-chat':
      return DEFAULT_CHAT_TOGGLES;
    case 'complaint':
      return DEFAULT_COMPLAINT_TOGGLES;
    default:
      return DEFAULT_SALES_TOGGLES;
  }
}
